import { Feature, FeatureCollection, MultiPolygon, Polygon, Position } from "geojson";
import { GeoBounds } from "./types";

export type LonLatRing = Position[];

export type ClipPolygon = LonLatRing[];

function isFiniteCoord(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isValidRing(ring: Position[] | undefined): ring is LonLatRing {
  if (!ring || ring.length < 3) {
    return false;
  }
  return ring.every(
    (pos) =>
      Array.isArray(pos) &&
      isFiniteCoord(pos[0]) &&
      isFiniteCoord(pos[1]) &&
      pos[0] >= -180 &&
      pos[0] <= 180 &&
      pos[1] >= -90 &&
      pos[1] <= 90
  );
}

function pushPolygonRings(
  polygons: ClipPolygon[],
  rings: Position[][] | undefined
): void {
  if (!rings?.length || !isValidRing(rings[0])) {
    return;
  }

  const clipRings: LonLatRing[] = [rings[0]];
  for (let i = 1; i < rings.length; i += 1) {
    if (isValidRing(rings[i])) {
      clipRings.push(rings[i]);
    }
  }
  polygons.push(clipRings);
}

export function extractTalhaoClipPolygons(
  collection: FeatureCollection | null | undefined
): ClipPolygon[] {
  if (!collection?.features?.length) {
    return [];
  }

  const polygons: ClipPolygon[] = [];

  collection.features.forEach((feature: Feature) => {
    const geometry = feature?.geometry;
    if (!geometry) {
      return;
    }

    if (geometry.type === "Polygon") {
      pushPolygonRings(polygons, (geometry as Polygon).coordinates);
      return;
    }

    if (geometry.type === "MultiPolygon") {
      (geometry as MultiPolygon).coordinates.forEach((polyRings) => {
        pushPolygonRings(polygons, polyRings);
      });
    }
  });

  return polygons;
}

export function buildClipPolygonsCacheKey(polygons: ClipPolygon[]): string {
  if (!polygons.length) {
    return "0";
  }

  let hash = polygons.length >>> 0;

  polygons.forEach((rings) => {
    hash = (Math.imul(hash, 31) + rings.length) >>> 0;
    rings.forEach((ring) => {
      hash = (Math.imul(hash, 31) + ring.length) >>> 0;
      const first = ring[0];
      const mid = ring[Math.floor(ring.length / 2)];
      const last = ring[ring.length - 1];
      [first, mid, last].forEach((pos) => {
        if (!pos) {
          return;
        }
        hash = (Math.imul(hash, 31) + Math.round(pos[0] * 1e5)) >>> 0;
        hash = (Math.imul(hash, 31) + Math.round(pos[1] * 1e5)) >>> 0;
      });
    });
  });

  return String(hash);
}

function projectToCanvas(
  lng: number,
  lat: number,
  bounds: GeoBounds,
  width: number,
  height: number
): { x: number; y: number } {
  const latSpan = bounds.north - bounds.south;
  const lngSpan = bounds.east - bounds.west;
  return {
    x: ((lng - bounds.west) / lngSpan) * width,
    y: ((bounds.north - lat) / latSpan) * height,
  };
}

export function drawTalhaoClipMask(
  ctx: CanvasRenderingContext2D,
  polygons: ClipPolygon[],
  bounds: GeoBounds,
  width: number,
  height: number
): void {
  ctx.clearRect(0, 0, width, height);
  if (!polygons.length) {
    return;
  }

  ctx.fillStyle = "#ffffff";

  polygons.forEach((rings) => {
    if (!rings.length) {
      return;
    }

    ctx.beginPath();
    rings.forEach((ring) => {
      const start = projectToCanvas(
        ring[0][0],
        ring[0][1],
        bounds,
        width,
        height
      );
      ctx.moveTo(start.x, start.y);
      for (let i = 1; i < ring.length; i += 1) {
        const point = projectToCanvas(
          ring[i][0],
          ring[i][1],
          bounds,
          width,
          height
        );
        ctx.lineTo(point.x, point.y);
      }
      ctx.closePath();
    });
    ctx.fill(rings.length > 1 ? "evenodd" : "nonzero");
  });
}

export function expandBoundsWithClipPolygons(
  bounds: GeoBounds,
  polygons: ClipPolygon[],
  paddingRatio = 0.01
): GeoBounds {
  if (!polygons.length) {
    return bounds;
  }

  let south = bounds.south;
  let north = bounds.north;
  let west = bounds.west;
  let east = bounds.east;

  polygons.forEach((rings) => {
    rings.forEach((ring) => {
      ring.forEach((pos) => {
        const lng = pos[0];
        const lat = pos[1];
        if (!isFiniteCoord(lng) || !isFiniteCoord(lat)) {
          return;
        }
        south = Math.min(south, lat);
        north = Math.max(north, lat);
        west = Math.min(west, lng);
        east = Math.max(east, lng);
      });
    });
  });

  const latPad = Math.max((north - south) * paddingRatio, 0.002);
  const lngPad = Math.max((east - west) * paddingRatio, 0.002);

  return {
    south: south - latPad,
    north: north + latPad,
    west: west - lngPad,
    east: east + lngPad,
  };
}

export function clipCanvasToTalhoes(
  source: HTMLCanvasElement,
  polygons: ClipPolygon[],
  bounds: GeoBounds
): HTMLCanvasElement {
  if (!polygons.length) {
    return source;
  }

  const width = source.width;
  const height = source.height;

  const mask = document.createElement("canvas");
  mask.width = width;
  mask.height = height;
  const maskCtx = mask.getContext("2d");
  if (!maskCtx) {
    return source;
  }
  drawTalhaoClipMask(maskCtx, polygons, bounds, width, height);

  const clipped = document.createElement("canvas");
  clipped.width = width;
  clipped.height = height;
  const ctx = clipped.getContext("2d");
  if (!ctx) {
    return source;
  }

  ctx.drawImage(source, 0, 0);
  ctx.globalCompositeOperation = "destination-in";
  ctx.drawImage(mask, 0, 0);
  ctx.globalCompositeOperation = "source-over";
  return clipped;
}
